import { getState, subscribe } from '../../../state.js';
import { selectDiagnosticViewModel } from '../../../state-selectors/diagnostic-selectors.js';

function normalizeInput(input = {}) {
  return input && input.sections && input.summary ? input : selectDiagnosticViewModel(input);
}

function severityClass(severity) {
  const value = String(severity || 'info').toLowerCase();
  if (value.includes('critical') || value.includes('error') || value.includes('high')) return 'is-critical';
  if (value.includes('warning') || value.includes('warn') || value.includes('medium')) return 'is-warning';
  return 'is-info';
}

function clipText(value, max = 180) {
  const text = String(value || '').trim();
  if (!Number.isFinite(max) || max <= 0 || text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}

function fieldLabel(field) {
  const value = String(field || '').trim();
  const labels = {
    title: '标题',
    bullets: '五点',
    description: '描述',
    searchTerms: 'Search Terms',
    qa: 'Q&A',
    aplus: 'A+',
    general: '全文',
  };
  return labels[value] || value || '全文';
}

function metricValue(value) {
  return value == null || value === '' ? '待评估' : String(value);
}

function issueImpact(item = {}) {
  const impact = String(item.businessImpact || '').toLowerCase();
  if (impact.includes('publish')) return '可能影响上架判断或刊登安全。';
  if (impact.includes('fact')) return '可能导致商品事实表达不准确。';
  if (impact.includes('seo') || impact.includes('traffic')) return '可能影响关键词执行和搜索流量。';
  if (impact.includes('conversion')) return '可能降低可读性和转化表现。';
  if (item.severity === 'critical') return '该问题会阻止当前 Listing 安全上架。';
  return '可能降低可读性、可信度或转化表现。';
}

function handlerLabel(item = {}) {
  if (item.evidenceRequired || item.decisionAuthority === 'semantic_review_hint') return '需要确认';
  return 'AI 可处理';
}

function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createDecisionMetric(label, value, emphasis = false, suffix = '', hint = '') {
  const metric = createElement('div', `diagnostic-decision-metric${emphasis ? ' is-emphasis' : ''}`);
  const strong = createElement('strong', '', metricValue(value));
  if (suffix) strong.append(createElement('small', '', suffix));
  metric.append(createElement('span', '', label), strong);
  if (hint) metric.append(createElement('em', '', hint));
  return metric;
}

function createIssueDetail(label, value) {
  const item = document.createElement('div');
  item.append(createElement('dt', '', label), createElement('dd', '', value));
  return item;
}

function createRequiredIssue(item = {}) {
  const article = createElement('article', `diagnostic-required-item ${severityClass(item.severity)}`);
  const head = createElement('div', 'diagnostic-required-head');
  head.append(
    createElement('span', 'diagnostic-field-badge', fieldLabel(item.field)),
    createElement('span', 'diagnostic-handler-badge', handlerLabel(item)),
  );
  const content = createElement('dl', 'diagnostic-required-content');
  content.append(
    createIssueDetail('问题', item.displayMessage || clipText(item.message || '当前 Listing 存在需要处理的问题。', 180)),
    createIssueDetail('影响', clipText(issueImpact(item), 120)),
    createIssueDetail('修改方式', item.actionSummary || '根据诊断建议生成优化稿，并在应用前完成安全检查。'),
  );
  article.append(head, content);
  return article;
}

function createRequiredIssues(decision = {}) {
  const issues = Array.isArray(decision.requiredIssues) ? decision.requiredIssues.slice(0, 3) : [];
  if (decision.publishDecisionReady === false) {
    return [createElement('div', 'diagnostic-required-empty is-pending', '诊断尚未完成，暂不能判断是否可以直接上架。')];
  }
  if (!decision.requiredCount) {
    return [createElement('div', 'diagnostic-required-empty', '当前没有必须修改项，可以直接上架；其余内容属于可选优化。')];
  }
  if (!issues.length) {
    return [createElement('div', 'diagnostic-required-empty', '已确认存在必须修改项，请查看完整诊断后重新生成优化稿。')];
  }
  return issues.map(createRequiredIssue);
}

function createPlanSteps(steps = []) {
  const list = createElement('ol', 'diagnostic-plan-list');
  steps.forEach((step, index) => {
    const item = document.createElement('li');
    item.append(
      createElement('span', 'diagnostic-plan-index', String(index + 1)),
      createElement('span', '', step),
    );
    list.append(item);
  });
  return list;
}

function createCopyQualityBreakdown(summary = {}, isDiagnosisPending = false) {
  const section = createElement('section', 'diagnostic-copy-quality');
  section.setAttribute('aria-labelledby', 'diagnostic-copy-quality-title');
  const title = createElement('h3', '', '文案评分依据');
  title.id = 'diagnostic-copy-quality-title';
  section.append(title);

  const status = String(summary.copyQualityStatus || 'unavailable').toLowerCase();
  const score = summary.copyQualityScore;
  if (isDiagnosisPending) {
    section.append(createElement('p', 'diagnostic-copy-quality-empty', '诊断完成后，将按商品说明、关键词、购买理由和模块完整度展示本轮文案评分。'));
    return section;
  }
  if (status === 'facts_required') {
    section.append(createElement('p', 'diagnostic-copy-quality-empty', '商品身份或核心事实待补充，本轮暂不评分；这不代表文案得分为 0。'));
    return section;
  }
  if (score == null) {
    section.append(createElement('p', 'diagnostic-copy-quality-empty', '本轮未生成文案评分，不以旧版评分或默认 0 分替代。可重新发起诊断。'));
    return section;
  }

  const dimensions = Array.isArray(summary.copyQualityDimensions) ? summary.copyQualityDimensions : [];
  if (dimensions.length) {
    const list = createElement('ul', 'diagnostic-copy-quality-list');
    dimensions.forEach((dimension) => {
      const item = createElement('li');
      item.append(
        createElement('span', '', dimension.label || '评分维度'),
        createElement('strong', '', Number.isFinite(dimension.score) && Number.isFinite(dimension.maxScore)
          ? `${dimension.score}/${dimension.maxScore}`
          : '待说明'),
      );
      list.append(item);
    });
    section.append(list);
  }
  const reasons = Array.isArray(summary.copyQualityReasons) ? summary.copyQualityReasons.filter(Boolean) : [];
  if (reasons.length) {
    const reasonList = createElement('ul', 'diagnostic-copy-quality-reasons');
    reasons.slice(0, 4).forEach((reason) => reasonList.append(createElement('li', '', reason)));
    section.append(reasonList);
  }
  if (!dimensions.length && !reasons.length) {
    section.append(createElement('p', 'diagnostic-copy-quality-empty', `本轮文案评分为 ${score}/100，未返回可展示的评分明细。`));
  }
  return section;
}

function buildRenderDecision(model = {}) {
  if (model.decision) return model.decision;
  const summary = model.summary || {};
  const sections = model.sections || {};
  const allItems = [
    ...(sections.compliance?.items || []),
    ...(sections.scoring?.items || []),
    ...(sections.revision?.items || []),
  ];
  const publishStatus = summary.publishStatus || '';
  const requiresChanges = ['needs_changes', 'blocked', 'facts_required'].includes(publishStatus)
    || sections.revision?.overview?.status === 'needs_revision';
  const explicitRequired = allItems.filter((item) => severityClass(item.severity) === 'is-critical');
  const fallbackRisk = sections.revision?.overview?.topRisks?.[0];
  const requiredIssues = explicitRequired.length
    ? explicitRequired
    : (requiresChanges && fallbackRisk ? [{
      field: fallbackRisk.field,
      severity: fallbackRisk.severity,
      message: fallbackRisk.issue,
      suggestion: fallbackRisk.recommendedAction,
      businessImpact: fallbackRisk.impactSummary,
    }] : []);
  const requiredCount = Math.max(explicitRequired.length, requiresChanges ? 1 : 0);
  const optionalCount = Math.max((summary.issueCount || allItems.length) - requiredCount, 0);
  const toneByStatus = {
    ready: 'is-ready',
    ready_with_improvements: 'is-ready',
    needs_changes: 'needs-changes',
    blocked: 'is-blocked',
    facts_required: 'is-blocked',
  };

  return {
    title: summary.publishStatusLabel || summary.statusLabel || '等待诊断',
    description: summary.optimizationSummary
      || (requiresChanges
        ? `当前有 ${requiredCount} 项必须修改，${optionalCount} 项建议优化。处理完成后可进入发布流程。`
        : '当前没有阻止上架的问题，可直接进入发布流程。'),
    tone: toneByStatus[publishStatus] || (requiresChanges ? 'needs-changes' : 'is-pending'),
    requiredCount,
    optionalCount,
    qualityScore: summary.copyQualityScore,
    publishDecisionReady: Boolean(publishStatus),
    requiredIssues,
    planSteps: ['按 Product Truth 校验商品身份和核心事实', '只处理模型明确指出的字段并生成候选稿', '保留原稿，确认后才写回'],
  };
}

export function renderDiagnosticCenter(input = {}) {
  const model = normalizeInput(input);
  const decision = buildRenderDecision(model);
  const isDiagnosisPending = model.isDiagnosisPending === true;
  const revisionError = model.sections?.revision?.error || '';
  const statusTone = model.hasAnySignals ? 'has-signals' : 'is-empty';
  const decisionTone = ['is-ready', 'needs-changes', 'is-blocked', 'is-pending'].includes(decision.tone)
    ? decision.tone
    : 'is-pending';
  const pendingDecisionTitle = model.diagnosisInFlight ? '诊断中' : '待检测';
  const pendingDecisionDescription = model.diagnosisInFlight
    ? '正在完成规则、语义和 SEO 检测；本轮结果生成后会一次性显示。'
    : '请开始诊断；完成后会显示本轮 Listing 的分数、结论与优化方案。';
  const root = createElement('section', `diagnostic-center ${statusTone}`);
  root.setAttribute('aria-label', '诊断中心');

  const decisionSection = createElement('section', `diagnostic-decision ${decisionTone}`);
  decisionSection.setAttribute('aria-labelledby', 'diagnostic-decision-title');
  const decisionCopy = createElement('div', 'diagnostic-decision-copy');
  const decisionTitle = createElement('h3', '', isDiagnosisPending ? pendingDecisionTitle : (decision.title || '等待诊断'));
  decisionTitle.id = 'diagnostic-decision-title';
  decisionCopy.append(
    createElement('span', 'diagnostic-decision-kicker', `当前诊断结论 · ${model.summary?.marketplaceLabel || '站点待确认'}`),
    decisionTitle,
    createElement('p', '', isDiagnosisPending ? pendingDecisionDescription : (decision.description || '诊断完成后，这里会明确说明当前 Listing 是否可以上架。')),
  );
  const metrics = createElement('div', 'diagnostic-decision-metrics');
  metrics.setAttribute('aria-label', '质量指标');
  const copyQualityStatus = String(model.summary?.copyQualityStatus || 'unavailable').toLowerCase();
  const copyQualityValue = isDiagnosisPending
    ? '待检测'
    : (copyQualityStatus === 'facts_required' ? '暂不评分' : (decision.qualityScore == null ? '未评分' : decision.qualityScore));
  const qualityNote = isDiagnosisPending
    ? '诊断完成前固定显示待检测状态，不展示旧分数或中途结果。'
    : (copyQualityStatus === 'facts_required'
      ? '商品身份或核心事实待补充，本轮暂不评分，不代表 0 分。'
      : (decision.qualityScore == null
        ? '本轮未生成文案评分，不以旧版评分或默认 0 分替代。'
        : '文案评分仅评当前 Listing；90 分 LQS 还需类目、属性、品牌和图片等刊登资料。'));
  metrics.append(
    createDecisionMetric('文案上架质量', copyQualityValue, true, isDiagnosisPending || decision.qualityScore == null ? '' : ' / 100'),
    createDecisionMetric('规则合规', isDiagnosisPending ? '待检测' : model.summary?.complianceStatusLabel, false, '', isDiagnosisPending ? '' : (model.summary?.complianceEvaluated ? `检查分 ${model.summary.complianceScore}/100` : '')),
    createDecisionMetric('评分依据', isDiagnosisPending ? '待检测' : (model.summary?.copyQualityDimensions?.length ? '4 个维度' : (copyQualityStatus === 'facts_required' ? '待补事实' : '未评分'))),
    createDecisionMetric('LQS 刊登资料', isDiagnosisPending ? '待检测' : '待采集', false, '', isDiagnosisPending ? '' : '类目、属性、品牌、图片'),
    createElement('p', 'diagnostic-score-note', qualityNote),
  );
  decisionSection.append(decisionCopy, metrics);

  const copyQualitySection = createCopyQualityBreakdown(model.summary || {}, isDiagnosisPending);

  const requiredSection = createElement('section', 'diagnostic-required');
  requiredSection.setAttribute('aria-labelledby', 'diagnostic-required-title');
  const requiredTitleRow = createElement('div', 'diagnostic-section-title-row');
  const requiredTitle = createElement('h3', '', `必须修改（${isDiagnosisPending ? '待检测' : String(decision.requiredCount || 0)}）`);
  requiredTitle.id = 'diagnostic-required-title';
  requiredTitleRow.append(requiredTitle);
  if (!isDiagnosisPending && decision.optionalCount) {
    requiredTitleRow.append(createElement('span', '', `${decision.optionalCount} 项建议优化`));
  }
  const requiredList = createElement('div', 'diagnostic-required-list');
  requiredList.append(...createRequiredIssues(decision));
  requiredSection.append(requiredTitleRow, requiredList);

  const planSection = createElement('section', 'diagnostic-plan');
  planSection.setAttribute('aria-labelledby', 'diagnostic-plan-title');
  const planTitle = createElement('h3', '', '系统将怎么改');
  planTitle.id = 'diagnostic-plan-title';
  const detailButton = createElement('button', 'diagnostic-details-trigger', '查看判断依据（按需）');
  detailButton.type = 'button';
  detailButton.dataset.openDiagnosticDetails = '';
  planSection.append(
    planTitle,
    createPlanSteps(decision.planSteps || []),
    createElement('p', 'diagnostic-plan-assurance', '系统只处理明确问题；未通过安全检查不会覆盖当前 Listing。'),
    detailButton,
  );
  root.append(decisionSection, copyQualitySection, requiredSection, planSection);

  if (revisionError) {
    const error = createElement('div', 'diagnostic-request-error');
    error.setAttribute('role', 'alert');
    error.append(createElement('strong', '', '改稿建议暂不可用'), createElement('span', '', revisionError));
    root.append(error);
  }
  return root;
}

export function renderDiagnosticCenterInto(container, input = {}) {
  if (!container) return;
  const model = normalizeInput(input);
  container.replaceChildren(renderDiagnosticCenter(model));

  const detailSummary = document.getElementById('diagnostic-details-summary');
  if (detailSummary) {
    detailSummary.textContent = `判断依据与高级诊断（${model.decision?.optionalCount || 0} 项建议）`;
  }

  container.querySelector('[data-open-diagnostic-details]')?.addEventListener('click', () => {
    const details = document.getElementById('diagnostic-details');
    if (!details) return;
    details.querySelectorAll('details').forEach((item) => {
      item.open = false;
    });
    details.open = true;
    details.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function collectDiagnosticInput() {
  return selectDiagnosticViewModel({
    complianceResult: getState('complianceResult'),
    scoringReport: getState('scoringReport'),
    revisionSuggestions: getState('revisionSuggestions'),
    revisionOverview: getState('revisionOverview'),
    revisionSuggestionsMeta: getState('revisionSuggestionsMeta'),
    revisionSuggestionsError: getState('revisionSuggestionsError'),
    listingDraftTitle: getState('listingDraftTitle'),
    listingDraftBullets: getState('listingDraftBullets'),
    listingDraftDescription: getState('listingDraftDescription'),
    listingDraftSearchTerms: getState('listingDraftSearchTerms'),
    listingDraftQa: getState('listingDraftQa'),
    marketplace: getState('marketplace'),
    diagnosticMarketplace: getState('diagnosticMarketplace'),
    productInfo: getState('productInfo'),
    businessContext: getState('businessContext'),
    keywordUsePolicies: getState('keywordUsePolicies'),
    competitorRoles: getState('competitorRoles'),
    listingGenerationMeta: getState('listingGenerationMeta'),
    diagnosisInFlight: getState('diagnosisInFlight'),
  });
}

export function initDiagnosticCenter() {
  const container = document.getElementById('diagnostic-center-root');
  if (!container) return;
  const detailsShell = document.getElementById('diagnostic-details');
  if (detailsShell) {
    detailsShell.open = false;
    detailsShell.querySelectorAll('details').forEach((item) => {
      item.open = false;
    });
    detailsShell.addEventListener('toggle', () => {
      if (!detailsShell.open) return;
      detailsShell.querySelectorAll('details').forEach((item) => {
        item.open = false;
      });
    });
  }

  const render = () => renderDiagnosticCenterInto(container, collectDiagnosticInput());
  render();

  for (const key of [
    'complianceResult',
    'scoringReport',
    'revisionSuggestions',
    'revisionSuggestionsMeta',
    'revisionSuggestionsError',
    'revisionOverview',
    'listingDraftTitle',
    'listingDraftBullets',
    'listingDraftDescription',
    'listingDraftSearchTerms',
    'listingDraftQa',
    'marketplace',
    'diagnosticMarketplace',
    'productInfo',
    'businessContext',
    'keywordUsePolicies',
    'competitorRoles',
    'listingGenerationMeta',
    'diagnosisInFlight',
  ]) {
    subscribe(`state:${key}Changed`, render);
  }

  subscribe('state:currentStepChanged', render);
}
